import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { selectedInsuranceCompaniesAtom } from '../store/agencyStore';
import { 
  processInsuranceApplications,
  validateBatchApplications,
  formatFileSize,
  calculateTotalFileSize,
  fetchAgencyInsuranceCompanies,
  fetchInsuranceFlags
} from '../services/api/InsuranceService';
import { INSURANCE_TYPE } from '../utils/insuranceTypeUtils';

// ファイル名の最大長
const MAX_FILENAME_LENGTH = 500;
// ファイル名表示の最大長さ
const MAX_DISPLAY_LENGTH = 100;

// ファイルアップロード機能のカスタムフック
const useFileUpload = (showConfirmDialog) => {
  const [uploadFiles, setUploadFiles] = useState({});

  const handleFileSelect = useCallback((company, fileType, e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const invalid = files.filter(file => (file.name || '').length > MAX_FILENAME_LENGTH);
      if (invalid.length > 0) {
        // 長すぎるファイル名のリストを作成
        const fileNameList = invalid.map(file => {
          const name = file.name;
          if (name.length > MAX_DISPLAY_LENGTH) {
            return `・${name.substring(0, MAX_DISPLAY_LENGTH)}...`;
          }
          return `・${name}`;
        }).join('\n');
        const message = `以下のファイル名が長すぎます（${MAX_FILENAME_LENGTH}文字以内にしてください）：\n\n${fileNameList}`;
        
        // 汎用ダイアログで表示
        showConfirmDialog(
          'ファイル名エラー',
          message,
          () => {
            // OKボタンのみ（確認後閉じる）
          },
          null,
          'error'
        );
        
        // input要素をリセット
        e.target.value = '';
        return;
      }

      // すべてのファイルが有効な場合のみアップロード処理を実行
      setUploadFiles(prev => {
        const newFiles = files.map(file => ({
          id: `${fileType}-${company}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          file: file
        }));
        
        if (!prev[company]) {
          const newState = {
            ...prev,
            [company]: {
              policy: [],
              newContract: [],
              maintenance: [],
              handbook: [],
              hikiuke: [],
              [fileType]: newFiles
            }
          };
          return newState;
        }
        
        const updatedState = {
          ...prev,
          [company]: {
            ...prev[company],
            [fileType]: [...(prev[company][fileType] || []), ...newFiles]
          }
        };
        return updatedState;
      });
      
      e.target.value = '';
    }
  }, [showConfirmDialog]);

  const triggerFileSelect = useCallback((inputId) => {
    document.getElementById(inputId).click();
  }, []);

  const removeFile = useCallback((company, fileType, fileId) => {
    setUploadFiles(prev => {
      if (!prev[company] || !prev[company][fileType]) return prev;
      
      const updatedFiles = prev[company][fileType].filter(item => item.id !== fileId);
      
      return {
        ...prev,
        [company]: {
          ...prev[company],
          [fileType]: updatedFiles
        }
      };
    });
  }, []);

  const initializeUploadFiles = useCallback((companies) => {
    const initialUploadState = {};
    companies.forEach(company => {
      const companyKey = typeof company === 'object' && company !== null ? company.name : company;
      initialUploadState[companyKey] = {
        policy: [],
        newContract: [],
        maintenance: [],
        handbook: [],
        hikiuke: []
      };
    });
    setUploadFiles(initialUploadState);
  }, []);

  return {
    uploadFiles,
    setUploadFiles,
    handleFileSelect,
    triggerFileSelect,
    removeFile,
    initializeUploadFiles
  };
};

// アコーディオン（展開/縮小）機能のカスタムフック
const useAccordion = () => {
  const [expandedCompanies, setExpandedCompanies] = useState({});

  const toggleCompanyExpansion = useCallback((companyName) => {
    setExpandedCompanies(prev => ({
      ...prev,
      [companyName]: !prev[companyName]
    }));
  }, []);

  const expandAllCompanies = useCallback((companyNames) => {
    const expandedState = {};
    companyNames.forEach(companyName => {
      const name = typeof companyName === 'object' && companyName !== null ? companyName.name : companyName;
      expandedState[name] = true;
    });
    setExpandedCompanies(expandedState);
  }, []);

  const collapseAllCompanies = useCallback(() => {
    setExpandedCompanies({});
  }, []);

  const initializeAccordion = useCallback((companies, defaultExpanded = false) => {
    const initialState = {};
    companies.forEach(company => {
      const companyKey = typeof company === 'object' && company !== null ? company.name : company;
      initialState[companyKey] = defaultExpanded;
    });
    setExpandedCompanies(initialState);
  }, []);

  // isLinkedに基づいてアコーディオン状態を初期化する関数を追加
  const initializeAccordionWithLinkedState = useCallback((companies) => {
    const initialState = {};
    companies.forEach(company => {
      const companyName = typeof company === 'object' && company !== null ? company.name : company;
      const isLinked = typeof company === 'object' && company !== null ? company.isLinked : false;
      
      // isLinkedがtrueなら閉じた状態（false）、falseなら展開状態（true）
      initialState[companyName] = !isLinked;
    });
    setExpandedCompanies(initialState);
  }, []);

  return {
    expandedCompanies,
    setExpandedCompanies,
    toggleCompanyExpansion,
    expandAllCompanies,
    collapseAllCompanies,
    initializeAccordion,
    initializeAccordionWithLinkedState
  };
};

// 送信進捗管理のカスタムフック
const useSubmissionProgress = () => {
  const [submissionProgress, setSubmissionProgress] = useState({
    isProcessing: false,
    stage: 'preparing',
    message: '',
    detail: '',
    progress: 0,
    uploadProgress: 0,
    timeRemaining: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetProgress = () => {
    setSubmissionProgress({
      isProcessing: false,
      stage: 'preparing',
      message: '',
      detail: '',
      progress: 0,
      uploadProgress: 0,
      timeRemaining: null
    });
    setIsSubmitting(false);
  };

  const updateProgress = (updates) => {
    setSubmissionProgress(prev => ({
      ...prev,
      ...updates
    }));
  };

  const startSubmission = () => {
    setIsSubmitting(true);
    setSubmissionProgress({
      isProcessing: true,
      stage: 'preparing',
      message: '申請を準備中...',
      detail: '',
      progress: 0,
      uploadProgress: 0,
      timeRemaining: null
    });
  };

  const calculateEstimatedTime = useCallback((totalSize, applicationCount) => {
    const uploadTimePerMB = 2;
    const processingTimePerApp = 1;
    
    const sizeInMB = totalSize / (1024 * 1024);
    const estimatedSeconds = (sizeInMB * uploadTimePerMB) + (applicationCount * processingTimePerApp);
    
    if (estimatedSeconds < 60) {
      return `約${Math.round(estimatedSeconds)}秒`;
    } else {
      const minutes = Math.floor(estimatedSeconds / 60);
      const seconds = Math.round(estimatedSeconds % 60);
      return `約${minutes}分${seconds}秒`;
    }
  }, []);

  const calculateRemainingTime = useCallback((startTime, progressPercentage) => {
    if (progressPercentage <= 0) return null;
    
    const elapsedTime = Date.now() - startTime;
    const totalEstimatedTime = elapsedTime * (100 / progressPercentage);
    const remainingTime = totalEstimatedTime - elapsedTime;
    
    if (remainingTime <= 0) return '完了間近';
    
    const remainingSeconds = Math.round(remainingTime / 1000);
    if (remainingSeconds < 60) {
      return `残り約${remainingSeconds}秒`;
    } else {
      const minutes = Math.floor(remainingSeconds / 60);
      const seconds = remainingSeconds % 60;
      return `残り約${minutes}分${seconds}秒`;
    }
  }, []);

  return {
    submissionProgress,
    isSubmitting,
    setSubmissionProgress,
    setIsSubmitting,
    resetProgress,
    updateProgress,
    startSubmission,
    calculateEstimatedTime,
    calculateRemainingTime
  };
};

// 確認ダイアログ管理のカスタムフック
const useConfirmDialog = () => {
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null,
    theme: 'default'
  });

  const showConfirmDialog = useCallback((title, message, onConfirm, onCancel = null, theme = 'default') => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
      onCancel,
      theme
    });
  }, []);

  const closeConfirmDialog = useCallback(() => {
    setConfirmDialog({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: null,
      onCancel: null,
      theme: 'default'
    });
  }, []);

  const handleConfirmDialogConfirm = useCallback(() => {
    if (confirmDialog.onConfirm) {
      confirmDialog.onConfirm();
    }
    closeConfirmDialog();
  }, [confirmDialog.onConfirm, closeConfirmDialog]);

  const handleConfirmDialogCancel = useCallback(() => {
    if (confirmDialog.onCancel) {
      confirmDialog.onCancel();
    }
    closeConfirmDialog();
  }, [confirmDialog.onCancel, closeConfirmDialog]);

  return {
    confirmDialog,
    showConfirmDialog,
    closeConfirmDialog,
    handleConfirmDialogConfirm,
    handleConfirmDialogCancel
  };
};

// 保険申請アップロード機能のバリデーションカスタムフック
const useInsuranceValidation = () => {
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [missingFiles, setMissingFiles] = useState([]);

  const closeConfirmationPopup = useCallback(() => {
    setShowConfirmationPopup(false);
  }, []);

  const validateFileRequirements = useCallback((selectedCompanies, uploadFiles, agencyInsuranceData, insuranceFlags = {}) => {
    let hasValidationError = false;
    let missingFilesList = [];

    const getCompanyFlagStatus = (company, relationData, flagKey) => {
      if (company && typeof company === 'object' && company[flagKey] !== undefined) {
        return !!company[flagKey];
      }

      const companyId = typeof company === 'object' && company !== null ? company.id : null;

      if (companyId && insuranceFlags && Object.prototype.hasOwnProperty.call(insuranceFlags, companyId)) {
        const flagInfo = insuranceFlags[companyId];
        if (flagInfo && Object.prototype.hasOwnProperty.call(flagInfo, flagKey)) {
          return !!flagInfo[flagKey];
        }
      }

      if (relationData && relationData[flagKey] !== undefined) {
        return !!relationData[flagKey];
      }

      return false;
    };
    
    for (const company of selectedCompanies) {
      const companyName = typeof company === 'object' && company !== null ? company.name : company;
      const companyId = typeof company === 'object' && company !== null ? company.id : null;
      const companyType = typeof company === 'object' && company !== null ? company.type : null;
      
      const hasPolicy = uploadFiles[companyName]?.policy?.length > 0;
      const hasNewContract = uploadFiles[companyName]?.newContract?.length > 0;
      const hasMaintenance = uploadFiles[companyName]?.maintenance?.length > 0;
      const hasHandbook = uploadFiles[companyName]?.handbook?.length > 0;
      const hasHikiuke = uploadFiles[companyName]?.hikiuke?.length > 0;
      const hasAnyFiles = hasPolicy || hasNewContract || hasMaintenance || hasHandbook || hasHikiuke;
      
      const relationData = agencyInsuranceData.find(item => 
        item.name === companyName || item.id === companyId
      );

      const isApproved = !!(
        relationData &&
        Array.isArray(relationData.approved_document_types) &&
        relationData.approved_document_types.length > 0
      );
      
      const hasHandbookFlag = getCompanyFlagStatus(company, relationData, 'has_handbook');
      const hasUploadableDocuments = companyType === INSURANCE_TYPE.SONPO ? hasHandbookFlag : true;

      if (!hasUploadableDocuments) {
        continue;
      }

      if (!isApproved && !hasAnyFiles) {
        hasValidationError = true;
        missingFilesList.push(`${companyName}のファイルをアップロードしてください。`);
      }
    }
    
    return {
      hasValidationError,
      missingFilesList
    };
  }, []);

  const executeValidation = useCallback((selectedCompanies, uploadFiles, agencyInsuranceData, insuranceFlags) => {
    const { hasValidationError, missingFilesList } = validateFileRequirements(
      selectedCompanies, 
      uploadFiles, 
      agencyInsuranceData,
      insuranceFlags
    );
    
    if (hasValidationError) {
      setMissingFiles(missingFilesList);
      setShowConfirmationPopup(true);
      return false;
    }
    
    setMissingFiles([]);
    setShowConfirmationPopup(true);
    return true;
  }, [validateFileRequirements]);

  return {
    showConfirmationPopup,
    missingFiles,
    closeConfirmationPopup,
    validateFileRequirements,
    executeValidation
  };
};

/**
 * 保険申請アップロード機能の統合カスタムフック（更新版）
 * @param {string} agencyId - 代理店ID
 * @returns {object} - 状態とハンドラー関数を含むオブジェクト
 */
export const useInsuranceUpload = (agencyId) => {
  const navigate = useNavigate();
  
  // 選択された保険会社の状態
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  
  // 代理店と保険会社の紐づけデータ
  const [agencyInsuranceData, setAgencyInsuranceData] = useState([]);
  const [insuranceFlags, setInsuranceFlags] = useState({});

  // Jotaiから選択された保険会社の情報を取得
  const [selectedCompaniesFromStore] = useAtom(selectedInsuranceCompaniesAtom);

  // 確認ダイアログを先に初期化（useFileUploadで使用するため）
  const {
    confirmDialog,
    showConfirmDialog,
    closeConfirmDialog,
    handleConfirmDialogConfirm,
    handleConfirmDialogCancel
  } = useConfirmDialog();

  // 細分化されたフックを使用
  const {
    uploadFiles,
    handleFileSelect,
    triggerFileSelect,
    removeFile,
    initializeUploadFiles
  } = useFileUpload(showConfirmDialog);

  const {
    expandedCompanies,
    setExpandedCompanies,
    toggleCompanyExpansion,
    expandAllCompanies,
    collapseAllCompanies,
    initializeAccordion,
    initializeAccordionWithLinkedState
  } = useAccordion();

  const {
    submissionProgress,
    isSubmitting,
    setSubmissionProgress,
    setIsSubmitting,
    resetProgress,
    updateProgress,
    startSubmission,
    calculateEstimatedTime,
    calculateRemainingTime
  } = useSubmissionProgress();

  const {
    showConfirmationPopup,
    missingFiles,
    closeConfirmationPopup,
    executeValidation
  } = useInsuranceValidation();

  // 代理店と保険会社の紐づけデータを取得
  useEffect(() => {
    const fetchAgencyData = async () => {
      try {
        const data = await fetchAgencyInsuranceCompanies(agencyId);
        setAgencyInsuranceData(data);
        console.log('代理店保険会社紐づけデータ:', data);
      } catch (error) {
        // エラーダイアログ表示はBaseApiServiceで行う
        // エラーコンソール出力は各ApiServiceで行う
      }
    };

    if (agencyId) {
      fetchAgencyData();
    }
  }, [agencyId]);

// 保険会社の文書タイプフラグを取得する関数
  const fetchAndSetInsuranceFlags = useCallback(async (companies) => {
    const ids = companies
      .map(company => (typeof company === 'object' && company !== null ? company.id : null))
      .filter(id => id !== null);

    if (ids.length > 0) {
      try {
        const flags = await fetchInsuranceFlags(ids);
        const flagMap = {};
        (Array.isArray(flags) ? flags : []).forEach(item => {
          if (item && item.insurance_company_id !== undefined) {
            flagMap[item.insurance_company_id] = {
              has_new_contract: !!item.has_new_contract,
              has_maintenance: !!item.has_maintenance,
              has_handbook: !!item.has_handbook,
              has_hikiuke: !!item.has_hikiuke
            };
          }
        });
        setInsuranceFlags(flagMap);
      } catch (error) {
        setInsuranceFlags({});
      }
    } else {
      setInsuranceFlags({});
    }
  }, []);

  useEffect(() => {
    // Jotaiから取得した情報で状態を初期化
    if (selectedCompaniesFromStore && selectedCompaniesFromStore.length > 0) {
      setSelectedCompanies(selectedCompaniesFromStore);
      console.log('選択された保険会社_02:', selectedCompaniesFromStore);
      
      // ファイルアップロード状態を初期化
      initializeUploadFiles(selectedCompaniesFromStore);
      
      // アコーディオン状態を代理店/保険会社の紐づき状況（isLinked）に基づいて初期化
      // isLinked: true → 閉じた状態（false）
      // isLinked: false → 展開状態（true）
      const initialAccordionState = {};
      selectedCompaniesFromStore.forEach(company => {
        const companyName = typeof company === 'object' && company !== null ? company.name : company;
        const isLinked = typeof company === 'object' && company !== null ? company.isLinked : false;
        
        // isLinkedがtrueなら閉じた状態（false）、falseなら展開状態（true）
        initialAccordionState[companyName] = !isLinked;
      });
      
      // 直接setExpandedCompaniesを呼び出して確実に初期化
      setExpandedCompanies(initialAccordionState);

      // 保険会社の文書タイプフラグを取得
      fetchAndSetInsuranceFlags(selectedCompaniesFromStore);
    }
  }, [selectedCompaniesFromStore, initializeUploadFiles, setExpandedCompanies, fetchAndSetInsuranceFlags]);

  // 完了時の画面遷移ハンドラ
  const handleCompleteNavigate = () => {
    resetProgress();
    closeConfirmationPopup();
    navigate(-1);
  };

  // 申請処理の続行
  const continueSubmission = useCallback(async (applications, validation, startTime) => {
    // Stage 3: アップロード準備
    const totalSize = calculateTotalFileSize(applications);
    const isMultipleApplications = applications.length > 1;
    
    updateProgress({
      stage: 'uploading',
      message: isMultipleApplications 
        ? `${applications.length}社の申請をアップロード中...` 
        : 'ファイルをアップロード中...',
      detail: `ファイル数: ${validation.summary.totalFiles}件 (${formatFileSize(totalSize)})`,
      progress: 30
    });

    // 推定時間を計算
    const estimatedTime = calculateEstimatedTime(totalSize, applications.length);
    
    updateProgress({
      detail: `ファイル数: ${validation.summary.totalFiles}件 (${formatFileSize(totalSize)}) - 推定時間: ${estimatedTime}`,
      timeRemaining: estimatedTime
    });

    console.log('申請処理開始:', {
      mode: isMultipleApplications ? 'batch' : 'single',
      applications: applications.length,
      totalFiles: validation.summary.totalFiles,
      totalSize: formatFileSize(totalSize),
      estimatedTime
    });

    // プログレス監視コールバック
    const onProgress = (progressInfo) => {
      const { loaded, total, percentage } = progressInfo;
      
      updateProgress({
        uploadProgress: percentage,
        progress: 30 + (percentage * 0.5), // 30-80%の範囲で進捗
        detail: `アップロード中: ${formatFileSize(loaded)} / ${formatFileSize(total)} (${percentage}%)`,
        timeRemaining: calculateRemainingTime(startTime, percentage)
      });
      
      console.log(`アップロード進捗: ${percentage}% (${formatFileSize(loaded)}/${formatFileSize(total)})`);
    };

    // Stage 4: 申請処理実行
    updateProgress({
      stage: 'processing',
      message: 'サーバーで処理中...',
      progress: 80
    });
    
    const result = await processInsuranceApplications(
      agencyId, 
      selectedCompanies, 
      uploadFiles,
      {
        onProgress: onProgress
      }
    );

    // Stage 5: 完了
    updateProgress({
      stage: 'complete',
      message: '申請処理完了',
      detail: '申請が正常に完了しました',
      progress: 100,
      uploadProgress: 100
    });
    
    console.log('申請処理完了:', result);
    
    // 結果の処理
    if (result.success) {
      const isBatchResult = result.batch_mode === true;
      
      if (isBatchResult) {
        // バッチ処理の結果
        const { successCount, failureCount, totalFiles } = result.summary;
        
        if (failureCount === 0) {
          updateProgress({
            message: '申請処理が完了しました！',
            detail: `成功: ${successCount}社\nファイル数: ${totalFiles}件`
          });
        } else {
          const failedApplications = result.results?.filter(r => !r.success) || [];
          const failureDetails = failedApplications
            .map(r => `・${r.companyName || `ID:${r.companyId}`}: ${r.error}`)
            .join('\n');
          
          updateProgress({
            message: '申請処理が完了しました',
            detail: `成功: ${successCount}社\n失敗: ${failureCount}社\nファイル数: ${totalFiles}件\n\n失敗した保険会社:\n${failureDetails}`
          });
        }
      } else {
        // 単一申請の結果
        updateProgress({
          message: '申請処理が完了しました！',
          detail: `ファイル数: ${result.uploaded_files}件`
        });
      }
      
    } else {
      throw new Error(result.message || '申請に失敗しました');
      // エラーダイアログ表示はBaseApiServiceで行う
      // エラーコンソール出力は各ApiServiceで行う
    }
  }, [agencyId, selectedCompanies, uploadFiles, updateProgress, calculateEstimatedTime, calculateRemainingTime]);

  // 申請を確定する
  const confirmSubmission = async () => {
    const startTime = Date.now();

    try {
      // 先に申請データを構築する
      const applications = selectedCompanies
        .map(company => {
          const companyName = typeof company === 'object' && company !== null ? company.name : company;
          const companyId = typeof company === 'object' && company !== null ? company.id : null;
          
          if (!companyId) {
            console.error(`保険会社 ${companyName} のIDが見つかりません。`);
            return null;
          }
          
          const newContractFiles = uploadFiles[companyName]?.newContract?.map(item => item.file) || [];
          const maintenanceFiles = uploadFiles[companyName]?.maintenance?.map(item => item.file) || [];
          const handbookFiles = uploadFiles[companyName]?.handbook?.map(item => item.file) || [];
          const hikiukeFiles = uploadFiles[companyName]?.hikiuke?.map(item => item.file) || [];
          const hasFiles = newContractFiles.length > 0 || maintenanceFiles.length > 0 || handbookFiles.length > 0 || hikiukeFiles.length > 0;

          if (!hasFiles) {
            console.log(`${companyName}はファイルがないためスキップしました`);
            return null;
          }

          return {
            companyId,
            companyName,
            newContractFiles,
            maintenanceFiles,
            handbookFiles,
            hikiukeFiles
          };
        })
        .filter(app => app !== null);

      // 0件なら進捗を開始せず、リセットする
      if (applications.length === 0) {
        closeConfirmationPopup();

        showConfirmDialog(
          'エラー',
          '申請するファイルがありません',
          () => {
            resetProgress();
          },
          () => {
            // キャンセルでも必ずリセット
            resetProgress();
          },
          'error'
        );
        return;
      }

      // 進捗開始
      startSubmission();

      // Stage 1: データ準備
      updateProgress({
        stage: 'preparing',
        message: '申請データを準備中...',
        progress: 10
      });

      // Stage 2: 事前検証
      updateProgress({
        stage: 'validating',
        message: '申請データを検証中...',
        progress: 20
      });
      
      const validation = validateBatchApplications(applications);
      
      if (!validation.isValid) {
        const errorMessage = validation.errors.join('\n');
        showConfirmDialog(
          '申請データエラー',
          `申請データに問題があります:\n\n${errorMessage}`,
          () => {
            resetProgress();
          },
          () => {
            resetProgress();
          },
          'error'
        );
        return;
      }
      
      // 警告確認 - confirmの代わりにカスタムダイアログを使用
      if (validation.warnings.length > 0) {
        const warningMessage = validation.warnings.join('\n');
        
        showConfirmDialog(
          '警告の確認',
          `以下の警告があります。続行しますか？\n\n${warningMessage}`,
          () => {
            // 警告を承認して処理を続行
            continueSubmission(applications, validation, startTime);
          },
          () => {
            // キャンセル時の処理
            resetProgress();
          }
        );
        return;
      }

      // 警告がない場合は直接処理を続行
      await continueSubmission(applications, validation, startTime);

    } catch (error) {
      // エラー時は自動でプログレス表示をリセット
      setTimeout(() => {
        resetProgress();
      }, 4000);
      closeConfirmationPopup();
    }
  };

  // 申請処理
  const handleSubmit = () => {
    console.log('アップロードファイル:', uploadFiles);
    console.log('代理店保険会社紐づけデータ:', agencyInsuranceData);
    
    // バリデーション実行
    executeValidation(selectedCompanies, uploadFiles, agencyInsuranceData, insuranceFlags);
  };

  // 戻るボタンハンドラ
  const handleReturn = () => {
    navigate(-1);
  };

  // プログレス処理のキャンセル
  const handleProgressCancel = () => {
    showConfirmDialog(
      '処理のキャンセル',
      '処理をキャンセルしますか？',
      () => {
        updateProgress({
          stage: 'cancelled',
          message: '処理をキャンセルしています...',
          progress: 0
        });
        
        // キャンセル処理
        setTimeout(() => {
          resetProgress();
          closeConfirmationPopup();
        }, 1000);
      }
    );
  };

  return {
    // 状態
    selectedCompanies,
    agencyInsuranceData,
    insuranceFlags,
    uploadFiles,
    showConfirmationPopup,
    missingFiles,
    isSubmitting,
    submissionProgress,
    confirmDialog,
    expandedCompanies,
    
    // ハンドラー関数
    handleFileSelect,
    triggerFileSelect,
    removeFile,
    closeConfirmationPopup,
    confirmSubmission,
    handleSubmit,
    handleReturn,
    handleProgressCancel,
    handleCompleteNavigate,
    showConfirmDialog,
    closeConfirmDialog,
    handleConfirmDialogConfirm,
    handleConfirmDialogCancel,
    
    // アコーディオン関連のハンドラー
    toggleCompanyExpansion,
    expandAllCompanies,
    collapseAllCompanies,
    setExpandedCompanies,
    initializeAccordionWithLinkedState,
    
    // ユーティリティ関数
    calculateEstimatedTime,
    calculateRemainingTime,
  };
};
